import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/base/ScrollToTop";
import JsonLd from "@/components/base/JsonLd";
import MangaCard from "@/components/base/MangaCard";
import { mangaList, categories, categorySubNav } from "@/mocks/manga";

export default function Category() {
  const { category } = useParams<{ category: string }>();
  const [activeSub, setActiveSub] = useState("all");
  const [sortBy, setSortBy] = useState("rating");

  useEffect(() => {
    setActiveSub("all");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [category]);

  const catInfo = categories.find((c) => c.key === category);
  const subNav = category ? categorySubNav[category as keyof typeof categorySubNav] : [];

  let filtered = mangaList.filter((m) => m.category === category);

  if (sortBy === "rating") {
    filtered = [...filtered].sort((a, b) => b.rating - a.rating);
  } else if (sortBy === "latest") {
    filtered = [...filtered].sort(
      (a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
    );
  } else if (sortBy === "views") {
    filtered = [...filtered].sort((a, b) => b.views - a.views);
  }

  const categoryLabels: Record<string, string> = {
    hanman: "韩漫",
    riman: "日漫",
    guoman: "国漫",
  };

  return (
    <>
      <ScrollToTop />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": `${catInfo?.label || ""} - 皮皮漫画网`,
          "url": `https://pipimanhua.com/category/${category}`,
          "description": catInfo?.subLabel || "海量漫画资源，实时更新",
          "isPartOf": {
            "@type": "WebSite",
            "name": "皮皮漫画网",
            "url": "https://pipimanhua.com/",
          },
        }}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            {
              label: catInfo ? catInfo.label : `${categoryLabels[category || ""] || ""}漫画`,
            },
          ]}
        />

        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading">
                {catInfo?.label || `${categoryLabels[category || ""] || ""}漫画`}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                {catInfo?.subLabel || "海量漫画资源，实时更新"}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-1 bg-background-200/70 rounded-full px-1 py-1">
                {subNav.map((sub) => (
                  <button
                    key={sub.key}
                    onClick={() => setActiveSub(sub.key)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      activeSub === sub.key
                        ? "bg-accent-500 text-background-50"
                        : "text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {sub.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">排序：</span>
                {[
                  { key: "rating", label: "评分最高" },
                  { key: "latest", label: "最近更新" },
                  { key: "views", label: "人气最热" },
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setSortBy(s.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === s.key
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-50 text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {filtered.map((manga) => (
                <MangaCard key={manga.id} {...manga} />
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300"></i>
                <p className="text-foreground-500 mt-4">暂无漫画数据</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}